import React from 'react'
import { Button, Cell, H1, Image, Paragraph, Row, Separator, FlowGrid } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import useInfoSheetOrientationCachedState from '../../../../../shared/hooks/useInfoSheetOrientationCachedState'
import noNamesImg from '../../../../images/no-names.svg'

/**
 * BlankState
 *
 * Displays a blank state UI when no persons/names exist in the current image.
 * Shows an illustration, heading, description, and "Add Name" button to create the first person.
 * The button is disabled for read-only collections.
 *
 * @component
 * @returns {JSX.Element}
 */
const BlankState = () => {
  const [t] = useTranslation()
  const { openOverlay } = useOverlayContext()
  const { isBottomSheet } = useInfoSheetOrientationCachedState()
  const { isReadOnlyCollection } = useCollectionContextProvider()
  const handleAddName = () => {
    openOverlay('addNamesOverlay')
  }

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
      <Separator size="nano" />
      <Row alignX="center">
        <Cell>
          <Image src={noNamesImg} alt="" width="auto" />
        </Cell>
      </Row>
      <H1 size="H5" centered>
        {t('select-person.blank-state.heading', 'No Names')}
      </H1>
      <Paragraph size="sm" centered>
        {t(
          'select-person.blank-state.paragraph',
          'Add a name to capture details that improve search and update the Family Tree.'
        )}
      </Paragraph>
      {isBottomSheet ? (
        <Row alignX="center">
          <Button
            emphasis="medium"
            onClick={handleAddName}
            data-testid="add-name-button"
            linkName="Add Name"
            disabled={isReadOnlyCollection}
          >
            {t('select-person.blank-state.add-name', 'Add Name')}
          </Button>
        </Row>
      ) : (
        <Button
          emphasis="medium"
          fullWidth
          onClick={handleAddName}
          data-testid="add-name-button"
          linkName="Add Name"
          disabled={isReadOnlyCollection}
        >
          {t('select-person.blank-state.add-name', 'Add Name')}
        </Button>
      )}
    </FlowGrid>
  )
}

export default BlankState
